import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../hooks/useAuth';

const contactSchema = z.object({
  firstname: z.string().min(1, 'Firstname is required'),
  lastname: z.string().min(1, 'Lastname is required'),
  email: z.string().email('Please enter a valid email'),
  inquiry: z.string().min(1, 'Inquiry type is required'),
  description: z.string().min(1, 'Description is required').max(5000),
});

type ContactForm = z.infer<typeof contactSchema>;

export function ContactPage() {
  const { user } = useAuth();
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      firstname: user?.firstname || '',
      lastname: user?.lastname || '',
      email: user?.email || '',
      inquiry: '',
      description: '',
    },
  });

  const contactMutation = useMutation({
    mutationFn: (data: ContactForm) => api.post('/api/help/contact', data),
    onSuccess: () => {
      setSubmitted(true);
      toast.success('Request added successfully.');
    },
    onError: () => toast.error('Failed to send message. Please try again.'),
  });

  async function onSubmit(data: ContactForm) {
    contactMutation.mutate(data);
  }

  if (submitted) {
    return (
      <div className="text-center p-8">
        <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" fill="#51c42b" className="mx-auto mb-4" viewBox="0 0 16 16">
          <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
        </svg>
        <h1 className="text-2xl font-bold mt-4">Thank you</h1>
        <p className="text-lg text-[#767676] mt-2">
          We have received your request. Connectfy team will communicate with you soon.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-3">Contact Us</h1>
      <p className="font-bold text-[#767676] mb-4">
        Please let us know what you are looking for.
      </p>

      <Card className="rounded-[20px] mb-4">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit(onSubmit)}>
            <h6 className="text-sm font-semibold text-[#cc0404] mb-4">Request Details</h6>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Firstname</label>
                <input
                  type="text"
                  className="w-full h-[42px] rounded-[4px] border border-gray-300 px-3 text-sm bg-gray-50"
                  readOnly
                  {...register('firstname')}
                />
                {errors.firstname && <p className="text-sm text-red-500 mt-1">{errors.firstname.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Lastname</label>
                <input
                  type="text"
                  className="w-full h-[42px] rounded-[4px] border border-gray-300 px-3 text-sm bg-gray-50"
                  readOnly
                  {...register('lastname')}
                />
                {errors.lastname && <p className="text-sm text-red-500 mt-1">{errors.lastname.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  className="w-full h-[42px] rounded-[4px] border border-gray-300 px-3 text-sm bg-gray-50"
                  readOnly
                  {...register('email')}
                />
                {errors.email && <p className="text-sm text-red-500 mt-1">{errors.email.message}</p>}
              </div>
              <div></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Inquiry</label>
                <select
                  className="w-full h-[42px] rounded-[4px] border border-gray-300 px-3 text-sm"
                  {...register('inquiry')}
                >
                  <option value="">Select inquiry type</option>
                  <option value="Connector">Connector Request</option>
                  <option value="Subscription">Subscription Related</option>
                  <option value="Others">General Inquiry</option>
                </select>
                {errors.inquiry && <p className="text-sm text-red-500 mt-1">{errors.inquiry.message}</p>}
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                className="w-full rounded-[4px] border border-gray-300 px-3 py-2 text-sm"
                rows={6}
                placeholder="Enter request details"
                {...register('description')}
              />
              {errors.description && <p className="text-sm text-red-500 mt-1">{errors.description.message}</p>}
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={isSubmitting || contactMutation.isPending}>
                {contactMutation.isPending ? 'Submitting...' : 'Submit'}
              </Button>
              <Button type="button" variant="secondary" onClick={() => window.history.back()}>
                Back
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}